import VoiceActorSelector from "./VoiceActorSelector";
import AudioAssignmentPanel from "./AudioAssignmentPanel";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  LinearProgress,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  MenuItem,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import PauseIcon from "@mui/icons-material/Pause";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import StopIcon from "@mui/icons-material/Stop";
import { getCourseVoiceEntries } from "../utils/courseVoices";
import {
  commitCourseAudioImport,
  prepareCourseAudioImport,
  refreshCourseAudioImportUrls,
  removeCourseVoice,
  saveCourseVoice,
  getCharacters,
  updateCourseAppearance,
} from "../service/service";
import {
  createUploadControl,
  uploadAudioFiles,
} from "../utils/audioUploadQueue";

const idFor = (file, index) =>
  `${index}-${file.name}-${file.size}-${file.lastModified}`;
const messageFrom = (error, fallback) =>
  error.response?.data?.err || error.response?.data?.message || fallback;
const emptyUploadState = {
  done: 0,
  total: 0,
  completed: [],
  active: [],
  failed: [],
};

const AudioVoicesManager = ({ course, onCourseChange }) => {
  const folderInputRef = useRef(null);
  const uploadControlRef = useRef(null);
  const errorRef = useRef(null);
  const [files, setFiles] = useState([]);
  const [prepared, setPrepared] = useState(null);
  const [uploadState, setUploadState] = useState(emptyUploadState);
  const [uploading, setUploading] = useState(false);
  const [paused, setPaused] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [destination, setDestination] = useState("");
  const [voiceName, setVoiceName] = useState("");
  const [editingVoiceName, setEditingVoiceName] = useState("");
  const [savingVoice, setSavingVoice] = useState(false);
  const [characterColor, setCharacterColor] = useState("#61FE6F");
  const [characterFile, setCharacterFile] = useState(null);
  const [characterId, setCharacterId] = useState("");
  const [characterName, setCharacterName] = useState("");
  const [headFile, setHeadFile] = useState(null);
  const [characters, setCharacters] = useState([]);
  const [overrideColor, setOverrideColor] = useState(false);
  const [courseColor, setCourseColor] = useState(
    course.defaultColor || "#61FE6F",
  );
  useEffect(() => {
    if (error) errorRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [error]);
  useEffect(() => {
    setCourseColor(course.defaultColor || "#61FE6F");
  }, [course.defaultColor]);
  useEffect(() => {
    getCharacters()
      .then((result) => setCharacters(result.data || []))
      .catch(() => setError("Could not load the character library."));
  }, []);

  useEffect(() => {
    const input = folderInputRef.current;
    input?.setAttribute("webkitdirectory", "");
    input?.setAttribute("directory", "");
    return () => uploadControlRef.current?.stop();
  }, []);

  const fileById = useMemo(
    () => new Map(files.map((file, index) => [idFor(file, index), file])),
    [files],
  );
  const voices = getCourseVoiceEntries(course);

  const manifestFor = () =>
    files.map((file, index) => {
      const root = file.webkitRelativePath?.split("/")[0] || "course-audio";
      const voice = destination.startsWith("voice:")
        ? destination.slice("voice:".length)
        : null;
      return {
        clientId: idFor(file, index),
        relativePath: [root, voice, file.name].filter(Boolean).join("/"),
        size: file.size,
      };
    });

  const selectFolder = (event) => {
    const chosen = Array.from(event.target.files || []).filter((file) =>
      /\.mp3$/i.test(file.name),
    );
    setFiles(chosen);
    setPrepared(null);
    setUploadState(emptyUploadState);
    setError(
      chosen.length ? "" : "Select a course folder that contains MP3 files.",
    );
    setNotice("");
  };

  const preflight = async () => {
    if (!files.length) return setError("Select the course root folder first.");
    if (!destination)
      return setError(
        "Choose where the audio should be saved before uploading.",
      );
    const descriptors = manifestFor();
    setLoading(true);
    setError("");
    try {
      const result = await prepareCourseAudioImport(course._id, descriptors);
      setPrepared({ ...result.data, manifest: descriptors });
      setUploadState({ ...emptyUploadState, total: result.data.files.length });
      setNotice("Preflight passed. Review the mapped files before uploading.");
    } catch (requestError) {
      const details = requestError.response?.data?.data?.errors || [];
      setError(
        details
          .map((item) => `${item.relativePath}: ${item.message}`)
          .join("\n") || messageFrom(requestError, "Preflight failed."),
      );
    } finally {
      setLoading(false);
    }
  };

  const beginUpload = async (retryIds) => {
    if (!prepared) return;
    setLoading(true);
    setUploading(true);
    setPaused(false);
    setError("");
    setNotice("");
    try {
      const previous = prepared.confirmations || [];
      const completedIds = new Set(previous.map((item) => item.clientId));
      let uploads = prepared.uploads.filter(
        (item) => !completedIds.has(item.clientId),
      );
      const refreshIds =
        retryIds ||
        (previous.length ? uploads.map((item) => item.clientId) : null);
      if (refreshIds?.length) {
        const refreshed = await refreshCourseAudioImportUrls(
          course._id,
          prepared.importToken,
          prepared.manifest,
          refreshIds,
        );
        uploads = refreshed.data.uploads;
      }
      const control = createUploadControl();
      uploadControlRef.current = control;
      const { confirmations, failed, stopped } = await uploadAudioFiles({
        uploads,
        fileById,
        previous,
        control,
        onProgress: (progress) =>
          setUploadState({
            ...progress,
            total: prepared.files.length,
          }),
      });
      const refreshedIds = refreshIds?.length ? new Set(refreshIds) : null;
      const nextUploads = refreshedIds
        ? [
            ...prepared.uploads.filter(
              (item) => !refreshedIds.has(item.clientId),
            ),
            ...uploads,
          ]
        : prepared.uploads;
      const next = { ...prepared, confirmations, uploads: nextUploads };
      setPrepared(next);
      if (stopped) {
        setNotice(
          `Upload stopped. ${confirmations.length} of ${prepared.files.length} files finished.`,
        );
        return;
      }
      if (failed.length)
        return setError(
          `${failed.length} file(s) failed. Refresh and retry them below.`,
        );
      const result = await commitCourseAudioImport(
        course._id,
        prepared.importToken,
        prepared.manifest,
        confirmations,
      );
      setPrepared({ ...next, status: result.data.status });
      setNotice(
        `Import committed: ${result.data.report?.linkedFiles || 0} audio file(s) verified across ${result.data.report?.linkedQuestions || 0} question(s).`,
      );
    } catch (requestError) {
      const details = requestError.response?.data?.data?.errors || [];
      setError(
        details
          .map(
            (item) => `${item.relativePath || "Audio link"}: ${item.message}`,
          )
          .join("\n") || messageFrom(requestError, "Audio import failed."),
      );
    } finally {
      uploadControlRef.current = null;
      setUploading(false);
      setPaused(false);
      setLoading(false);
    }
  };

  const togglePause = () => {
    const control = uploadControlRef.current;
    if (!control) return;
    if (paused) {
      control.resume();
      setPaused(false);
      setNotice("Upload resumed.");
    } else {
      control.pause();
      setPaused(true);
      setNotice("Upload paused. Files already in progress will finish.");
    }
  };

  const stopUpload = () => {
    uploadControlRef.current?.stop();
    setPaused(false);
    setNotice("Stopping upload…");
  };

  const saveVoice = async (event) => {
    event.preventDefault();
    if (!voiceName.trim()) return setError("Enter a voice name.");
    const formData = new FormData();
    // The URL identifies the existing actor; the edited label is separate.
    if (editingVoiceName) formData.append("newName", voiceName.trim());
    if (overrideColor) formData.append("characterColor", characterColor);
    else formData.append("clearCharacterColor", "true");
    if (characterId && !characterFile)
      formData.append("characterId", characterId);
    if (characterName) formData.append("displayName", characterName);
    if (headFile) formData.append("headFile", headFile);
    if (characterFile) formData.append("file", characterFile);
    setLoading(true);
    setSavingVoice(true);
    setError("");
    try {
      const result = await saveCourseVoice(
        course._id,
        editingVoiceName || voiceName.trim(),
        formData,
      );
      onCourseChange({
        ...course,
        voices: result.course?.voices ?? course.voices,
        voiceCharacters:
          result.course?.voiceCharacters ?? course.voiceCharacters,
        defaultColor: result.course?.defaultColor ?? course.defaultColor,
      });
      const savedName = voiceName.trim().replace(/\s+/g, " ");
      window.dispatchEvent(new CustomEvent("course-voice-registered", {
        detail: { courseId: course._id, course: result.course,
          previousName: editingVoiceName, voiceName: savedName },
      }));
      if (editingVoiceName && editingVoiceName !== savedName) {
        if (destination === `voice:${editingVoiceName}`) setDestination(`voice:${savedName}`);
        setPrepared(null);
        setUploadState(emptyUploadState);
      }
      // Saving succeeded even if a separate library refresh is unavailable.
      getCharacters().then(library => setCharacters(library.data || []))
        .catch(() => setError("Voice saved, but the character library could not be refreshed."));
      setCharacterId("");
      setCharacterName("");
      setHeadFile(null);
      setOverrideColor(false);
      setVoiceName("");
      setEditingVoiceName("");
      setCharacterFile(null);
      setNotice(editingVoiceName && editingVoiceName !== savedName
        ? `Voice renamed to ${savedName}. Linked audio updated.` : "Voice character saved.");
    } catch (requestError) {
      setError(messageFrom(requestError, "Could not save voice character."));
    } finally {
      setLoading(false);
      setSavingVoice(false);
    }
  };

  const deleteVoice = async (name) => {
    if (
      !window.confirm(
        `Remove ${name}? This is only allowed when it has no audio references.`,
      )
    )
      return;
    setLoading(true);
    try {
      const result = await removeCourseVoice(course._id, name);
      onCourseChange({
        ...course,
        voices: result.course?.voices ?? course.voices,
        voiceCharacters:
          result.course?.voiceCharacters ?? course.voiceCharacters,
        defaultColor: result.course?.defaultColor ?? course.defaultColor,
      });
      setNotice("Voice character removed.");
    } catch (requestError) {
      setError(messageFrom(requestError, "Could not remove voice character."));
    } finally {
      setLoading(false);
    }
  };

  const retryIds = uploadState.failed.map((item) => item.clientId);
  const completedIds = new Set([
    ...(prepared?.confirmations || []).map((item) => item.clientId),
    ...uploadState.completed,
  ]);
  const activeIds = new Set(uploadState.active);
  const failedById = new Map(
    uploadState.failed.map((item) => [item.clientId, item.message]),
  );
  const fileStatus = (clientId) => {
    if (prepared?.status === "committed" || completedIds.has(clientId)) {
      return (
        <Stack
          direction="row"
          spacing={0.5}
          alignItems="center"
          color="success.main"
        >
          <CheckCircleOutlineIcon fontSize="small" />
          <span>Uploaded</span>
        </Stack>
      );
    }
    if (activeIds.has(clientId)) {
      return (
        <Stack direction="row" spacing={0.5} alignItems="center">
          <CircularProgress size={16} />
          <span>Uploading</span>
        </Stack>
      );
    }
    if (failedById.has(clientId)) {
      return (
        <Stack
          direction="row"
          spacing={0.5}
          alignItems="center"
          color="error.main"
          title={failedById.get(clientId)}
        >
          <ErrorOutlineIcon fontSize="small" />
          <span>Failed</span>
        </Stack>
      );
    }
    return (
      <Typography variant="body2" color="text.secondary">
        Pending
      </Typography>
    );
  };

  return (
    <Stack spacing={3}>
      <AudioAssignmentPanel course={course} onCourseChange={onCourseChange} refreshKey={notice} />
      <Paper variant="outlined" sx={{ p: 3 }}>
        <Typography variant="h6">Bulk audio import</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Select the course audio folder and its voice actor. Preflight blocks
          invalid or ambiguous files before any upload.
        </Typography>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          alignItems="center"
        >
          <Button
            variant="outlined"
            disabled={uploading}
            onClick={() => folderInputRef.current?.click()}
          >
            Select course folder
          </Button>
          <input
            ref={folderInputRef}
            hidden
            type="file"
            multiple
            accept=".mp3,audio/mpeg"
            onChange={selectFolder}
          />
          <Typography variant="body2">
            {files.length
              ? `${files.length} MP3 file(s) selected`
              : "No folder selected"}
          </Typography>
          <VoiceActorSelector courseId={course._id} course={course} value={destination.startsWith("voice:") ? destination.slice(6) : ""} onCourseChange={onCourseChange} disabled={loading} onChange={voice => {
            setDestination(voice ? `voice:${voice}` : ""); setPrepared(null); setUploadState(emptyUploadState); setError(""); setNotice("");
          }} />
          <Button
            variant="contained"
            disabled={!files.length || !destination || loading}
            onClick={preflight}
          >
            {loading && !prepared ? (
              <CircularProgress size={18} />
            ) : (
              "Run preflight"
            )}
          </Button>
        </Stack>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mt: 1, display: "block" }}
        >
          Every selected MP3 is saved in the selected actor’s Azure subfolder.
          Existing fallback URLs remain available for older app versions.
        </Typography>
        {prepared && (
          <>
            <Divider sx={{ my: 2 }} />
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              alignItems="center"
            >
              <Typography>
                {prepared.summary.total} mapped · {prepared.summary.newFiles}{" "}
                new · {prepared.summary.replacements} replacement(s)
              </Typography>
              {prepared.status === "committed" ? (
                <Alert severity="success">Committed</Alert>
              ) : (
                !uploading && (
                  <Button
                    variant="contained"
                    disabled={loading}
                    onClick={() =>
                      prepared.summary.replacements &&
                      !prepared.confirmations?.length
                        ? setConfirmOpen(true)
                        : beginUpload()
                    }
                  >
                    {prepared.confirmations?.length
                      ? "Upload remaining and commit"
                      : "Upload and commit"}
                  </Button>
                )
              )}
              {uploading && (
                <Button
                  variant="outlined"
                  startIcon={paused ? <PlayArrowIcon /> : <PauseIcon />}
                  onClick={togglePause}
                >
                  {paused ? "Resume" : "Pause"}
                </Button>
              )}
              {uploading && (
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<StopIcon />}
                  onClick={stopUpload}
                >
                  Stop
                </Button>
              )}
              {!!retryIds.length && !uploading && (
                <Button
                  onClick={() => beginUpload(retryIds)}
                  disabled={loading}
                >
                  Retry failed uploads
                </Button>
              )}
            </Stack>
            <LinearProgress
              variant="determinate"
              sx={{ mt: 2 }}
              value={
                prepared.files.length
                  ? (uploadState.done / prepared.files.length) * 100
                  : 0
              }
            />
            <Typography variant="caption">
              {uploadState.done} of {uploadState.total || prepared.files.length}{" "}
              uploaded
            </Typography>
            <Box sx={{ maxHeight: 320, overflow: "auto", mt: 1 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>File</TableCell>
                    <TableCell>Question</TableCell>
                    <TableCell>Audio target</TableCell>
                    <TableCell>Voice</TableCell>
                    <TableCell>Action</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {prepared.files.map((file) => (
                    <TableRow key={file.clientId}>
                      <TableCell>{file.relativePath}</TableCell>
                      <TableCell>
                        U{file.unitOrder} · L{file.levelOrder} · Q
                        {file.questionOrder} ({file.questionType})
                      </TableCell>
                      <TableCell>{file.target}</TableCell>
                      <TableCell>{file.voiceName}</TableCell>
                      <TableCell>{file.action}</TableCell>
                      <TableCell>{fileStatus(file.clientId)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </>
        )}
      </Paper>
      <Paper variant="outlined" sx={{ p: 3 }}>
        <Typography variant="h6">Voice characters</Typography>
        {!voices.length && (
          <Alert severity="info" sx={{ mt: 2 }}>
            No voice actors are registered yet. Add a voice actor above, then choose
            a character below.
          </Alert>
        )}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          sx={{ my: 2, alignItems: { sm: "center" } }}
        >
          <TextField
            label="Default Color"
            type="color"
            value={courseColor}
            onChange={(event) => setCourseColor(event.target.value)}
            sx={{ width: { xs: "100%", sm: 220 } }}
            InputLabelProps={{ shrink: true }}
          />
          <Button
            disabled={loading}
            onClick={async () => {
              setLoading(true);
              setError("");
              try {
                const result = await updateCourseAppearance(
                  course._id,
                  courseColor,
                );
                onCourseChange({
                  ...course,
                  defaultColor: result.data.defaultColor,
                  voices: result.data.voices ?? course.voices,
                  voiceCharacters: result.data.voiceCharacters,
                });
                setNotice("Default Color saved.");
              } catch (error) {
                setError(messageFrom(error, "Could not save Default Color."));
              } finally {
                setLoading(false);
              }
            }}
          >
            Save Default Color
          </Button>
          <Button href="/characters" target="_blank" rel="noreferrer">
            Open character library
          </Button>
        </Stack>
        <Typography variant="body2" color="text.secondary">
          These characters identify the voice in editing and preview controls.
          Character PNGs are stored in Azure assets.
        </Typography>
        <List dense>
          {voices.map(([name, config]) => (
            <ListItem
              key={name}
              secondaryAction={
                <>
                  <Button
                    size="small"
                    disabled={loading}
                    onClick={() => {
                      setEditingVoiceName(name);
                      setVoiceName(name);
                      setCharacterColor(config.characterColor);
                      setCharacterId(config.characterId || "");
                      setCharacterFile(null);
                      setHeadFile(null);
                      setCharacterName("");
                      setOverrideColor(
                        Boolean(
                          course.voiceCharacters?.[name]?.hasColorOverride,
                        ),
                      );
                    }}
                  >
                    Edit
                  </Button>
                  <Button
                    color="error"
                    size="small"
                    onClick={() => deleteVoice(name)}
                    disabled={loading}
                  >
                    Remove
                  </Button>
                </>
              }
            >
              <ListItemAvatar>
                <Avatar
                  src={config.characterUrl}
                  sx={{ bgcolor: config.characterColor }}
                >
                  {name[0]}
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={name}
                secondary={
                  config.characterName
                    ? `Character: ${config.characterName} · ${config.characterColor}`
                    : `No character assigned · ${config.characterColor}`
                }
              />
            </ListItem>
          ))}
        </List>
        <Box
          component="form"
          onSubmit={saveVoice}
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" },
            gap: 1,
            alignItems: "center",
            mt: 1,
          }}
        >
          <TextField
            label="Voice name"
            size="small"
            value={voiceName}
            disabled={loading}
            onChange={(event) => setVoiceName(event.target.value)}
            helperText={editingVoiceName ? `Editing ${editingVoiceName}. Renaming also updates all linked audio.` : "Add a new voice actor, or select Edit above."}
          />
          <TextField
            label="Character Color"
            size="small"
            type="color"
            value={characterColor}
            onChange={(event) => setCharacterColor(event.target.value)}
            disabled={loading || !overrideColor}
          />
          <FormControlLabel
            control={
              <Checkbox
                disabled={loading}
                checked={overrideColor}
                onChange={(event) => setOverrideColor(event.target.checked)}
              />
            }
            label="Override Default Color"
          />
          <FormControl size="small" disabled={loading}>
            <InputLabel id="voice-character-label">Character</InputLabel>
            <Select
              label="Character"
              labelId="voice-character-label"
              value={characterId}
              onOpen={() =>
                getCharacters()
                  .then((result) => setCharacters(result.data || []))
                  .catch(() => setError("Could not refresh characters."))
              }
              onChange={(event) => {
                setCharacterId(event.target.value);
                setCharacterFile(null);
                setHeadFile(null);
              }}
            >
              <MenuItem value="">
                {course.voiceCharacters?.[editingVoiceName || voiceName]?.characterId
                  ? "Keep current / upload new"
                  : "Default Listening / choose or upload"}
              </MenuItem>
              {characterId &&
                !characters.some(
                  (character) => character._id === characterId,
                ) && (
                  <MenuItem value={characterId}>
                    {course.voiceCharacters?.[editingVoiceName || voiceName]?.character
                      ?.displayName || "Current character"}
                  </MenuItem>
                )}
              {characters.map((character) => (
                <MenuItem key={character._id} value={character._id}>
                  <Avatar
                    src={character.headUrl || character.characterUrl}
                    sx={{ mr: 1, width: 28, height: 28 }}
                  />
                  {character.displayName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button component="label" variant="outlined" disabled={loading}>
            {characterFile?.name || "Character PNG"}
            <input
              hidden
              type="file"
              accept="image/png"
              onChange={(event) => {
                setCharacterFile(event.target.files?.[0] || null);
                setCharacterId("");
              }}
            />
          </Button>
          {characterFile && (
            <>
              <TextField
                label="New character name"
                value={characterName}
                onChange={(event) => setCharacterName(event.target.value)}
                required
              />
              <Button component="label">
                {headFile?.name || "Head PNG (optional)"}
                <input
                  hidden
                  type="file"
                  accept="image/png"
                  onChange={(event) =>
                    setHeadFile(event.target.files?.[0] || null)
                  }
                />
              </Button>
            </>
          )}
          <Button type="submit" variant="contained" disabled={loading}>
            {savingVoice ? "Updating voice…" : "Save voice"}
          </Button>
          {editingVoiceName && (
            <Button disabled={loading} onClick={() => {
              setEditingVoiceName(""); setVoiceName(""); setCharacterId("");
              setCharacterFile(null); setHeadFile(null); setCharacterName("");
              setOverrideColor(false);
            }}>Cancel edit</Button>
          )}
        </Box>
      </Paper>
      {error && (
        <Alert ref={errorRef} severity="error" sx={{ whiteSpace: "pre-wrap" }}>
          {error}
        </Alert>
      )}
      {notice && <Alert severity="success">{notice}</Alert>}
      <Dialog open={savingVoice} disableEscapeKeyDown fullWidth maxWidth="xs">
        <DialogTitle>Updating voice actor</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            {editingVoiceName && editingVoiceName !== voiceName.trim()
              ? "Renaming the voice and updating its linked recordings. Please wait…"
              : "Saving the voice actor and character settings. Please wait…"}
          </Typography>
          <LinearProgress />
        </DialogContent>
      </Dialog>
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Replace existing audio?</DialogTitle>
        <DialogContent>
          Preflight found {prepared?.summary.replacements || 0} existing audio
          URLs that will be replaced. Files omitted from this import remain
          untouched.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => {
              setConfirmOpen(false);
              beginUpload();
            }}
          >
            Upload and commit
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default AudioVoicesManager;
